import React, { useContext } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import { TranslationContext } from "../contexts/TranslationContext";

import * as Yup from "yup";
import axios from "axios"; // Import axios
import Swal from "sweetalert2";
import API_BASE_URL from "../apiConfig";
import LangSwitcher from "../components/LangSwitcher";

const Register = () => {
  const { translations, selectedLanguage, routes } = useContext(TranslationContext);
  const [loading, setLoading] = React.useState(false);

  // Validation schema for registration
  const validationSchema = Yup.object({
    user_name: Yup.string()
      .min(1, translations.nameReqMin)
      .max(20, translations.nameReqMax)
      .matches(/^\S*$/, translations.nameSpaces)
      .required(translations.nameReqMin),

    email: Yup.string()
      .email(translations.invalidEmail)
      .notRequired()
      .nullable(),
    password: Yup.string()
      .required(translations.passReq)
      .min(10, translations.passReqMin)
      .max(20, translations.passReqMax),
    password_confirmation: Yup.string()
      .oneOf([Yup.ref("password"), null], translations.passNotMatch)
      .required(translations.cpassReq),
  });

  // Formik Hook
  const formik = useFormik({
    initialValues: {
      user_name: "",
      email: "",
      password: "",
      password_confirmation: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true); // Show loader
      try {
        // Registration API call
        const registerResponse = await axios.post(
          `${API_BASE_URL}/api/register`,
          {
            user_name: values.user_name,
            email: values.email,
            password: values.password,
            password_confirmation: values.password_confirmation,
            locale: selectedLanguage, // Sending locale

          }
        );

        console.log("Registration Response:", registerResponse.data);
        // Automatic login
        const loginResponse = await axios.post(
          `${API_BASE_URL}/api/login`,
          {
            user_name: values.user_name,
            password: values.password,
            locale: selectedLanguage,
          }
        );
        console.log("Login Response:", loginResponse.data);
        if (loginResponse.data.token) {
          localStorage.setItem("token", loginResponse.data.token);
          localStorage.setItem("locale", selectedLanguage); // Save locale for future API calls

        }
        setLoading(false);
        window.location.href = routes.home;
      } catch (error) {
        console.error("API Error:", error.response?.data || error.message);
        Swal.fire({
          toast: true,
          position: "top-end",
          title:
            error.response?.data?.errors
              ? Object.entries(error.response.data.errors)
                .map(([key, messages]) => `${messages.join(", ")}`)
                .join("\n")
              : "Registration failed. Please try again.",
          icon: "error",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        setLoading(false);
      }
    },

  });


  return (
    <>
      <Helmet>
        <title>{translations.register} - O.A.V.L.I.T.E</title>
      </Helmet>
      <div className="navbar-header container-fluid px-5 pt-2 mb-5">
        <div className="w-100 emptyBox1"></div>
        <div className="text-center logo-wrapper auth-wrapper pt-3">
          <Link to="/" className="logo-link">
            <img
              src="../images/oavlite-black.png"
              className="img-fluid mx-auto text-center d-block d-logo mb-3"
              alt="oavlite"
            />
          </Link>
          <p className="mb-2 fw-500 tagline__1">
            {translations.taglineV1}
          </p>

        </div>
        <Link to={routes.login} className="btn btn-light bgprimary border-0 rounded-pill oavlite-text-primary auth--btn px-4 ">
          {translations.logIn}
        </Link>
        <LangSwitcher />
      </div>

      <div className="login-wrapper text-center position-relative mb-5">
        <div className="container">
          <div className="center-content mb-5">
            <h1 className="mb-3">{translations.freeRegister}</h1>
            <h4 className="mb-3 mb-md-5 bgprimary border-0 p-3 fw-600 uniqueSocial">{translations.uniqueSocial}</h4>

            <form onSubmit={formik.handleSubmit}>
              <div className="form-group">
                <label htmlFor="user_name" className="form-col-lable mb-2">
                  {translations.nameLabel}
                </label>
                <input
                  type="text"
                  name="user_name"
                  className="form-control rounded-pill input-field"
                  onChange={formik.handleChange}
                  value={formik.values.user_name}
                />
                {formik.errors.user_name && <p className="text-warning ">{formik.errors.user_name}</p>}
              </div>
              <div className="form-group d-none">
                <label htmlFor="email" className="form-col-lable mb-2">
                  {translations.emailLabel}
                </label>
                <input
                  type="email"
                  name="email"
                  className="form-control rounded-pill input-field"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.email && formik.errors.email ? (
                  <div className="text-warning ">{formik.errors.email}</div>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-col-lable mb-2">
                  {translations.passwordLabel}
                </label>
                <input
                  type="password"
                  name="password"
                  className="form-control rounded-pill input-field"
                  onChange={formik.handleChange}
                  value={formik.values.password}
                />
                {formik.errors.password && <p className="text-warning ">{formik.errors.password}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="password_confirmation" className="form-col-lable mb-2">
                  {translations.cpasswordLabel}
                </label>
                <input
                  type="password"
                  name="password_confirmation"
                  className="form-control rounded-pill input-field"
                  onChange={formik.handleChange}
                  value={formik.values.password_confirmation}
                />
                {formik.errors.password_confirmation && <p className="text-warning ">{formik.errors.password_confirmation}</p>}
              </div>
              <button type="submit" className="auth-theme-btn btn btn-light bgprimary border-0 mx-auto theme-btn" disabled={loading}>
                {loading ? translations.registering : translations.validateRegister}
              </button>
            </form>
          </div>
          {/* <p className="fw-500">  1 MILLION <br /> {translations.millionTagline} <br /> {translations.millionTagline2}</p> */}
        </div>
      </div>

      <footer className="text-center">
        <p>
          <Link className="color-primary" to={routes.legalNotices}>
            CGU
          </Link>
        </p>
      </footer>
    </>
  );
};

export default Register;
